/* Quran Foundation content API (v4), reached through /api/qf/* — Vite proxies
   it in dev, functions/api/qf/[[path]].ts in production. The service worker
   caches responses stale-while-revalidate, which is what makes reading work
   offline once a page has been opened (or downloaded in Settings → Storage). */
import chapters from '@/data/chapters.json';
import juzs from '@/data/juzs.json';
import pages from '@/data/pages.json';
import quranAdhkar from '@/data/quranAdhkar.json';

export interface Chapter { id: number; n: string; ar: string; t: string; v: number; p: [number, number]; place: 'M' | 'D'; bis: boolean }
export const CHAPTERS = chapters as Chapter[];
export const chapter = (id: number) => CHAPTERS[id - 1];
export const JUZS = juzs as unknown as { n: number; first: string; last: string; map: Record<string, string> }[];

export interface Word {
  id: number;
  position: number;
  char_type_name: 'word' | 'end';
  code_v1: string;
  code_v2: string;
  text_uthmani: string;
  /** King Fahd Complex (QPC) Ḥafṣ text — what the Uthmanic Hafs font is made for (see script.ts). */
  text_qpc_hafs?: string;
  /** Page and line in each Madinah print: v1 = 1405 (QCF v1 glyphs), v2 = 1421 (QCF v2 / tajweed). */
  v1_page?: number;
  v2_page?: number;
  line_v1?: number;
  line_v2?: number;
  /** The API's defaults (1421 print) — only a fallback for the fields above. */
  line_number: number;
  page_number: number;
  translation?: { text: string };
  transliteration?: { text: string };
}

export interface Verse {
  id: number;
  verse_key: string;
  verse_number: number;
  page_number: number;
  juz_number: number;
  hizb_number: number;
  text_uthmani: string;
  text_qpc_hafs?: string;
  text_indopak_nastaleeq?: string;
  text_uthmani_tajweed?: string;
  sajdah_number: number | null;
  words: Word[];
  translations?: { id: number; resource_id: number; text: string }[];
}

/* Fields are named explicitly: the API's page_number / line_number follow the
   1421 print, while verses/by_page uses the 1405 page breaks — 36 pages hold
   different āyāt in the two prints (most of Juz ʿAmma). */
export const WORD_FIELDS = 'code_v1,code_v2,text_uthmani,text_qpc_hafs,v1_page,v2_page,line_v1,line_v2,line_number,page_number';
export const VERSE_FIELDS = 'text_uthmani,text_qpc_hafs,text_indopak_nastaleeq,text_uthmani_tajweed';

async function qf<T>(path: string): Promise<T> {
  const r = await fetch(`/api/qf/${path}`);
  if (!r.ok) throw new Error(`Quran API ${r.status}`);
  return r.json() as Promise<T>;
}

/* ── Madinah page layout ─────────────────────────────────────────────── */

/** Which print's page breaks: v1 = 1405 (Madani 1405 glyphs), v2 = 1421 (everything else). */
export type PageLayout = 'v1' | 'v2';
const PAGE_START: Record<PageLayout, number[]> = { v1: pages.v1, v2: pages.v2 };
/** Id (1–6236) of each surah's first āyah. */
const FIRST_ID: number[] = [0];
CHAPTERS.forEach((_, i) => { FIRST_ID[i + 1] = i ? FIRST_ID[i] + CHAPTERS[i - 1].v : 1; });

export const parseKey = (k: string) => { const [s, a] = k.split(':').map(Number); return { surah: s, ayah: a }; };
export const verseId = (key: string) => { const { surah, ayah } = parseKey(key); return FIRST_ID[surah] + ayah - 1; };

/** The āyah ids on a page (every page holds whole āyāt). */
export function pageRange(page: number, layout: PageLayout): [number, number] {
  const s = PAGE_START[layout];
  return [s[page - 1], page < 604 ? s[page] - 1 : 6236];
}

/** The page an āyah is on, in the given print. */
export function pageOfVerse(key: string, layout: PageLayout = 'v2'): number {
  const id = verseId(key), s = PAGE_START[layout];
  let lo = 0, hi = s.length - 1;
  while (lo < hi) { const mid = (lo + hi + 1) >> 1; if (s[mid] <= id) lo = mid; else hi = mid - 1; }
  return lo + 1;
}

export const wordPage = (w: Word, layout: PageLayout) => (layout === 'v1' ? w.v1_page : w.v2_page) ?? w.page_number;
export const wordLine = (w: Word, layout: PageLayout) => (layout === 'v1' ? w.line_v1 : w.line_v2) ?? w.line_number;

const pageQuery = (page: number, translationId: number, layout: PageLayout) =>
  `verses/by_page/${page}?words=true&word_fields=${WORD_FIELDS}&fields=${VERSE_FIELDS}&translations=${translationId}&word_translation_language=en&per_page=50&mushaf=${layout === 'v1' ? 2 : 1}`;

/** The āyāt on a page of the given print. The API may use the other print's
    page breaks, so a neighbouring page is fetched too when āyāt are missing. */
export async function versesByPage(page: number, translationId: number, layout: PageLayout = 'v2'): Promise<Verse[]> {
  const [a, b] = pageRange(page, layout);
  const got = new Map<number, Verse>();
  const add = (vs: Verse[]) => vs.forEach((v) => got.set(v.id, v));
  add((await qf<{ verses: Verse[] }>(pageQuery(page, translationId, layout))).verses);
  if (!got.has(a) && page > 1) add((await qf<{ verses: Verse[] }>(pageQuery(page - 1, translationId, layout))).verses);
  if (!got.has(b) && page < 604) add((await qf<{ verses: Verse[] }>(pageQuery(page + 1, translationId, layout))).verses);
  return [...got.values()].filter((v) => v.id >= a && v.id <= b).sort((x, y) => x.id - y.id);
}

export async function versesByChapter(ch: number, translationId: number): Promise<Verse[]> {
  const total = chapter(ch).v;
  const per = 50;
  const n = Math.ceil(total / per);
  const all = await Promise.all(
    Array.from({ length: n }, (_, i) =>
      qf<{ verses: Verse[] }>(
        `verses/by_chapter/${ch}?words=true&word_fields=${WORD_FIELDS}&fields=${VERSE_FIELDS}&translations=${translationId}&word_translation_language=en&per_page=${per}&page=${i + 1}`,
      ).then((j) => j.verses),
    ),
  );
  return all.flat();
}

/** Just the texts of a few āyāt (no words/translation) — used by the Qurʾānic adhkar. */
export interface VerseTexts { verse_key: string; text_uthmani: string; text_qpc_hafs?: string; text_indopak_nastaleeq?: string; text_uthmani_tajweed?: string }
/* The āyāt the adhkar and ruqyah recite are bundled (scripts/fetch-quran-adhkar.mjs),
   so they always show the muṣḥaf's own text, offline too. */
const BUNDLED = quranAdhkar as Record<string, VerseTexts>;
export const bundledVerseTexts = (keys: string[]): VerseTexts[] | undefined => (keys.every((k) => BUNDLED[k]) ? keys.map((k) => BUNDLED[k]) : undefined);
export async function verseTexts(keys: string[]): Promise<VerseTexts[]> {
  return bundledVerseTexts(keys) ?? Promise.all(keys.map((k) => qf<{ verse: VerseTexts }>(`verses/by_key/${k}?fields=${VERSE_FIELDS}`).then((j) => j.verse)));
}

/* Strip translation footnote markup (<sup foot_note=…>) — rendered as plain text. */
export const cleanTranslation = (html: string) => html.replace(/<sup[^>]*>.*?<\/sup>/g, '').replace(/<[^>]+>/g, '');

export function juzOfKey(verseKey: string): number {
  const { surah, ayah } = parseKey(verseKey);
  for (const j of JUZS) {
    const range = j.map[String(surah)];
    if (!range) continue;
    const [a, b] = range.split('-').map(Number);
    if (ayah >= a && ayah <= b) return j.n;
  }
  return 1;
}
